import Immutable from 'immutable';

import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { transforms, rulesCache, get, sortArray } from 'ctm-util-functions';

import { getRadioComponentStateSuffix } from '../utils';
import styles from './radio_button_container.css';
import { Anchor } from '../../anchor/anchor';
import { FormGroupNotification, toggleNotification } from '../form_group_notification/form_group_notification';

class RadioButtonContainer extends Component {

    /**
     * Set the default showAll state
     * @param {Object} props The component properties
     */
    constructor(props) {
        super(props);
        this.state = {
            // For preload and loadJourney data, if user selected an option that is after the initial limit position,
            // we want to show all options
            showAll: this.isCheckedInputIndexOverLimit()
        };
        this.notificationNodeArray = [];
    }

    componentDidMount = () => {
        this.updateNotification();
    };

    componentDidUpdate = () => {
        this.updateNotification();
    };

    /**
     * Show or hide the notification text, if notificationText has been supplied
     * and the value for the radio button corresponds to a condition that should cause the notification text to be displayed or hidden
     */
    updateNotification = () => {
        const { value } = this.props;
        const { notificationProps } = this.state;
        if (this.notificationNodeArray.length && !notificationProps) {
            const notificationNodeSelected = this.notificationNodeArray.filter((node) => value === get(node, 'props.optionProps.value'));
            if (notificationNodeSelected.length) {
                // eslint-disable-next-line react/no-find-dom-node
                this.setState(toggleNotification({ target: ReactDOM.findDOMNode(notificationNodeSelected[0]) }, this));
            }
        }
    };

    /**
     * Toggle the showAll state to show more or less options
     * @param {Object} event - The event object.
     */
    toggleShowAll = (event) => {
        event.preventDefault();
        this.setState((state) => {
            return {
                showAll: !state.showAll
            };
        });
    };

    /**
     * Check if the checked input's index is lager than the initial limit
     * @returns {Boolean} True if we have initial limit and the checked option's index is larger, false otherwise
     */
    isCheckedInputIndexOverLimit = () => {
        const { schema, children } = this.props;
        const initialOptionsLimit = schema.field.initialOptionsLimit;

        // if the limit isn't defined in the schema, don't bother compare
        if (!initialOptionsLimit) {
            return false;
        }

        const checkedInputIndex = this.getSelectedOptionIndex(children);

        return checkedInputIndex >= initialOptionsLimit;
    };

    /**
     * Get the Index of selected option.
     * this method was not moved to util library due to optionProps is specific
     * of the children elements of this components.
     * @param  {Array} children - An array of the radio button children.
     * @returns {Number} The index for the selected option.
     */
    getSelectedOptionIndex = (children) => {
        return children.findIndex((child) => {
            // all radio button schema should have optionProps and value
            return child.props.optionProps.value === child.props.value;
        });
    };

    /**
     * Activate the tooltip panel.
     * @param {Node} triggerElement - The HTML element the tooltip is positioned from.
     */
    activateTooltip = (triggerElement) => {
        this.setState({ tooltipTrigger: triggerElement });
    };

    /**
     * Deactivate the tooltip panel.
     */
    deactivateTooltip = () => {
        this.setState({ tooltipTrigger: false });
    };

    /**
     * Stores notification text nodes in an array
     * @param {Node} node - The radio group element
     */
    addNodeToNotificationNodeArray = (node) => {
        this.notificationNodeArray.push(node);
    };

    /**
     * Adds additional props to all children to add functionality not available from
     * the element factory like local state.
     * @param {Array} children - An array of the radio button children.
     * @returns {Array} The array new set to children.
     */
    addPropsToChildren = (children) => children.map((child) => {
        const { tooltipTrigger } = this.state;
        const { value, notificationText } = get(child, 'props.optionProps', {});

        // Additional props for all children.
        const props = {
            ...child.props,
            activateTooltip: this.activateTooltip,
            deactivateTooltip: this.deactivateTooltip,
            tooltipTrigger: get(tooltipTrigger, 'id', '').startsWith(value) ? tooltipTrigger : false
        };

        this.notificationNodeArray = [];

        // Add callback method for children with notification text.
        if (notificationText) {
            const newProps = {
                ...props,
                ref: this.addNodeToNotificationNodeArray,
                eventProps: {
                    onClick: (event) => { this.setState(toggleNotification(event, this)); }
                }
            };
            return React.cloneElement(child, newProps);
        }

        return { ...child, props };
    });

    /**
     * Sort the child elements of this container based on the conditional ordinality specified in the schema. If there
     * is no ordinality defined, just return the children without sorting
     * @param {Array} children - An array of the radio button children.
     * @returns {Array} - The array of sorted child element.
     */
    sortChildren = (children) => {
        const ordinalityRule = get(this.props, 'rules.ordinality');
        if (!ordinalityRule) {
            return children;
        }
        const elementRule = rulesCache.getElementRuleForId(ordinalityRule.id);
        const orderBy = ordinalityRule.result ? elementRule.rule : 'default';
        const sortConfig = Immutable.Map({ sortBy: `props.optionProps.order.${orderBy}`, sortDir: 'asc', sortType: 'numeric' });
        return sortArray(sortConfig, Immutable.List(children));
    };

    /**
     * Create the toggle element
     * @param {Boolean} showAll - state of the toggle to indicate if the component should show all of the options
     * @param {Number} initialOptionsLimit - number of options to show for the initial load
     * @returns {Anchor|null} Anchor component
     */
    getToggleElement = (showAll, initialOptionsLimit) => {
        const { schema } = this.props;
        const { showMoreOptionsLabel = 'Show more options ', showLessOptionsLabel = 'Show less options ', optionsLabelIconPosition = 'after' } =            schema.field;
        const toggleElementText = showAll ? showLessOptionsLabel : showMoreOptionsLabel;
        const toggleIcon = showAll ? 'ctm_minus' : 'ctm_plus';
        return initialOptionsLimit ? (
            <Anchor
                className={styles.toggle}
                label={toggleElementText}
                iconPosition={optionsLabelIconPosition}
                icon={toggleIcon}
                onClick={this.toggleShowAll}
            />
        ) : null;
    };

    /**
     * Render the component if it has the props to do so
     * @returns {FormGroupNotification|Null} A component or null
     */
    getFormGroupNotification = () => {
        const { children = [], selectedValues } = this.props;
        const { notificationProps } = this.state;
        const checkedInputIndex = this.getSelectedOptionIndex(children);
        const notificationText = get(children[checkedInputIndex], 'props.optionProps.notificationText', null);

        return notificationText ? (
            <FormGroupNotification
                notificationProps={notificationProps}
                notificationText={notificationText}
                selectedValues={selectedValues}
            />
        ) : null;
    };

    /**
     * Renders an input element based on the supplied props
     * @returns {Node|null} component -  The complete input element
     */
    render = () => {
        const { schema, children, rules } = this.props;
        const { showAll } = this.state;
        const type = schema.field.type;
        let showToggleElement = true;
        let initialOptionsLimit = schema.field.initialOptionsLimit;
        if (initialOptionsLimit === undefined && get(this.props, 'rules.initialOptionsLimit.result')) {
            const elementRule = rulesCache.getElementRuleForId(rules.initialOptionsLimit.id);
            initialOptionsLimit = transforms[elementRule.props.valueTransform]();
            if (elementRule.props.hideToggleElement) {
                showToggleElement = false;
            }
        }
        const validationClass = getRadioComponentStateSuffix(this.props).substring(1);
        const className = classNames(
            styles.root,
            styles[`${type}`],
            styles[validationClass],
            schema.variant && styles[`variant_${schema.variant}`]
        );

        const newChildren = this.addPropsToChildren(children);
        const formElements = initialOptionsLimit && !showAll ? newChildren.slice(0, initialOptionsLimit) : newChildren;
        const sortedFormElements = this.sortChildren(formElements);

        return (
            <>
                <div className={className}>
                    {sortedFormElements}
                    {showToggleElement ? this.getToggleElement(showAll, initialOptionsLimit) : null}
                </div>
                {this.getFormGroupNotification()}
            </>
        );
    };

}

RadioButtonContainer.propTypes = {
    schema: PropTypes.object.isRequired,
    children: PropTypes.node.isRequired,
    value: PropTypes.string.isRequired,
    rules: PropTypes.object,
    selectedValues: PropTypes.object
};

export { RadioButtonContainer };
